/**
 * 任务过滤和排序逻辑
 */
import { ref, computed } from 'vue'
import type { Ref } from 'vue'
import type { Task } from '@/types/task.domain'
import type { TaskFilter, TaskSort } from '@/types/task.dto'

export function useTaskFilter(tasks: Ref<Task[]>) {
    // 过滤和排序状态
    const filter = ref<TaskFilter>({})
    const sort = ref<TaskSort>({ field: 'createdAt', order: 'desc' })

    /**
     * 过滤后的任务列表
     */
    const filteredTasks = computed(() => {
        let result = tasks.value

        // 应用过滤条件
        if (filter.value.status && filter.value.status !== 'all') {
            // 强制转换为 any[] 以兼容 TaskStatus | TaskStatus[]
            const statuses = Array.isArray(filter.value.status)
                ? filter.value.status
                : [filter.value.status]

            // 处理特殊状态值
            if (statuses.includes('!completed' as any)) {
                // 排除已完成的任务
                result = result.filter(task => task.status !== 'completed')
            } else if (statuses.includes('expired' as any)) {
                // "已过期"是后端计算的虚拟状态（已完成+资产已过期），
                // 后端已经按此逻辑过滤，本地不再二次过滤
            } else {
                // 正常状态过滤
                result = result.filter(task => statuses.includes(task.status))
            }
        }

        if (filter.value.type) {
            const types = Array.isArray(filter.value.type)
                ? filter.value.type
                : [filter.value.type]

            result = result.filter(task => {
                return types.some(t => {
                    // Support prefix match: 'drama' matches 'drama_render', 'drama_export' etc.
                    if (t === 'drama') return task.type.startsWith('drama')
                    return task.type === t
                })
            })
        }

        if (filter.value.keyword) {
            const keyword = filter.value.keyword.toLowerCase()
            result = result.filter(task =>
                task.title.toLowerCase().includes(keyword) ||
                (task.description && task.description.toLowerCase().includes(keyword))
            )
        }

        if (filter.value.dateRange) {
            const [startDate, endDate] = filter.value.dateRange
            result = result.filter(task => {
                const taskDate = new Date(task.createdAt)
                return taskDate >= new Date(startDate) && taskDate <= new Date(endDate)
            })
        }

        if (filter.value.voices && filter.value.voices.length > 0) {
            result = result.filter(task => {
                // 从第一个 item 的 segment 获取 voice
                const firstItem = task.items?.[0]
                const voice = firstItem?.segment?.voice
                if (task.type === 'single' && voice) {
                    return filter.value.voices!.includes(voice)
                }
                return true
            })
        }

        // 应用排序
        result.sort((a, b) => {
            const field = sort.value.field as keyof Task
            const order = sort.value.order

            let aValue = a[field]
            let bValue = b[field]

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase()
                bValue = bValue.toLowerCase()
            }

            if (aValue === undefined || bValue === undefined) {
                return 0
            }

            if (order === 'asc') {
                return aValue > bValue ? 1 : -1
            } else {
                return aValue < bValue ? 1 : -1
            }
        })

        return result
    })

    /**
     * 设置过滤条件
     */
    const setFilter = (newFilter: TaskFilter) => {
        filter.value = { ...filter.value, ...newFilter }
    }

    /**
     * 设置排序条件
     */
    const setSort = (newSort: TaskSort) => {
        sort.value = newSort
    }

    return {
        filter,
        sort,
        filteredTasks,
        setFilter,
        setSort
    }
}
